import { FlexBox } from '@dixa/lib-shared-habla';
import type {
  ConversationMessageDeliveryEvent,
  ConversationMessageDeliveryEventStatus,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';

import { getConversationMessageDeliveryStatusAttributes } from 'utils/conversation';  
import { formatContactPointValue } from 'utils/conversation/deliveryStatus/formatContactPoint';

import { DeliveryStatusMessage } from './components/deliveryStatusMessage';

type ConversationMessageDeliveryReportProps = PropsWithClassName & {
  /** The computed delivery status. */
  computedStatus: ConversationMessageDeliveryEventStatus;
  /** The extended conversation message delivery events. */
  deliveryEvents: readonly ConversationMessageDeliveryEvent[];
};

/** Represents the component for displaying a list of delivery events. */
export const ConversationMessageDeliveryReport = ({
  className,
  computedStatus,
  deliveryEvents,
}: ConversationMessageDeliveryReportProps): JSX.Element => {
  const deliveries = useMemo(() => {
    const contactPointDeliveries = Object.entries(
      groupBy(deliveryEvents, 'contact_point_value'),
    );
    const isReadAtLeastByOne =
      contactPointDeliveries.length > 1 && computedStatus === 'seen';

    return isReadAtLeastByOne ? (
      <DeliveryStatusMessage
        icon={getConversationMessageDeliveryStatusAttributes('seen').icon}
        label="Read at least by one of the recipients"
      />
    ) : (
      contactPointDeliveries.map(([key, events]) => {
        const { icon, labelTemplate } =
          getConversationMessageDeliveryStatusAttributes(events[0].event_type);

        // Format the contact point value (email or phone number)
        const formattedContactPoint = formatContactPointValue(events[0]);

        const label =
          typeof labelTemplate === 'string'
            ? labelTemplate
            : labelTemplate({
                info: events[0].info ? `: ${events[0].info}` : '',
              });

        return (
          <DeliveryStatusMessage
            key={key}
            icon={icon}
            label={label}
            contactPoint={formattedContactPoint}
          />
        );
      })
    );
  }, [computedStatus, deliveryEvents]);

  return (
    <FlexBox className={className} gap="100" flexDirection="column">
      {deliveries}
    </FlexBox>
  );
};
